import { forEach, isArray } from 'lodash-es'
import { fetchGet } from '@lib/fetch/client'
import { mapboxToken } from '@lib/config'
import { Base64 } from 'js-base64'

/**
 * 根据关键字获取地址列表
 * 
 * @param {String} keyword 查询条件
 * @param {Object} opt 查询参数
 * 
 * @return {Promise}
 */
export function getAddresByKeyword(keyword, proximity, limit = 10, fetchOption = {}) {
  return new Promise((resolve) => {
    if(!keyword) {
      resolve([])
    } else {
      const option = [
        `country=${process.env.NEXT_PUBLIC_COUNTRY.toLowerCase()}`,
        'types=address,place,district,locality,neighborhood',
        `access_token=${mapboxToken}`,
        `limit=${limit}`
      ]
      if (proximity) {
        option.push(`proximity=${proximity}`)
      }
      const api = `${process.env.NEXT_PUBLIC_MAPBOX_API}/geocoding/v5/mapbox.places/${keyword}.json?`
      const url = api + option.join('&')
      
      fetchGet('call-mapbox-api', { url }, fetchOption)
        .then(({ features }) => resolve(features))
    }
  })
}

function decode(str, precision) {
  let index = 0
  let lat = 0
  let lng = 0
  let shift = 0
  let result = 0
  let byte = null
  let latitudeChange
  let longitudeChange
  const coordinates = []
  const factor = 10 ** (precision || 5)

  while (index < str.length) {
    byte = null
    shift = 0
    result = 0

    do {
      byte = str.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)

    latitudeChange = ((result & 1) ? ~(result >> 1) : (result >> 1))

    shift = 0
    result = 0

    do {
      byte = str.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)

    longitudeChange = ((result & 1) ? ~(result >> 1) : (result >> 1))

    lat += latitudeChange
    lng += longitudeChange

    coordinates.push([lat / factor, lng / factor])
  }

  return coordinates
}

function getDirectionsMapbox(startCoord, endCoord, type = 'driving') {
  const queryStr = encodeURIComponent([startCoord.join(','), ';', endCoord.join(';')].join(''))
  const option = [
    'geometries=polyline',
    'steps=true',
    'overview=full',
    `access_token=${mapboxToken}`,
  ]
  const url = `${process.env.NEXT_PUBLIC_MAPBOX_API}/directions/v5/mapbox/${type}/${queryStr}.json?${option.join('&')}`

  return fetchGet('call-mapbox-api', { url }).then(({ uuid, routes }) => {
    const legs = routes?.[0]?.legs?.[0]
    const geometry = routes?.[0]?.geometry
    
    return {
      uuid,
      geometry,
      coordinates: decode(geometry, 5).map((c) => c.reverse()),
      distance: legs.distance,
      duration: legs.duration,
      summary: legs.summary
    }
  })
}

function getDirectionsGoogle(startCoord, endCoord) {
  const origin = [...startCoord].reverse().join(',')
  const destination = [...endCoord].reverse().join(',')

  const option = [
    `origin=${origin}`,
    `destination=${destination}`,
    'mode=transit',
    `key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_ACCESSTOKEN}`,
  ]
  const url = `https://maps.googleapis.com/maps/api/directions/json?${option.join('&')}`
  return fetchGet('call-mapbox-api', { url }).then((d) => {
    const uuid = Base64.encode(d?.routes?.[0]?.overview_polyline?.points)
    const coordinates = []
    const summary = []
    let distance = 0
    let duration = 0

    forEach(isArray(d?.routes) ? d.routes : [], (route) => {
      const coords = decode(route.overview_polyline.points, 5).map((c) => c.reverse())
      coordinates.push(...coords)

      forEach(route.legs, (leg) => {
        distance += leg.distance.value
        duration += leg.duration.value

        forEach(leg.steps, (step) => {
          if(summary.length < 2) {
            summary.push(String(step.html_instructions).replace(/<[^>]+>/gi, ''))
          }
        })
      })
    })
    
    return {
      uuid,
      coordinates,
      distance,
      duration,
      summary: summary.join(',')
    }
  })
}

/**
 * 获取两点间的导航数据
 * 
 * @param {Array} startCoord 开始坐标
 * @param {Array} endCoord 结束坐标
 * @param {String} type 类型(driving,walking,cycling)
 * 
 * @return {Promise}
 */
export function getDirections(startCoord, endCoord, type = 'driving') {
  return type === 'driving-traffic'
    ? getDirectionsGoogle(startCoord, endCoord?.[0])
    : getDirectionsMapbox(startCoord, endCoord, type)
}

/**
 * 根据经唯独，获取国家标识
 */
export function getCountry({ lng, lat }) {
  return new Promise((resolve) => {
    const baseApi = process.env.NEXT_PUBLIC_MAPBOX_API
    const url = `${baseApi}/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxToken}`

    fetchGet('call-mapbox-api', { url }).then((data) => {
      const countryFeature = data.features.find(
        (feature) => feature.place_type.includes('country')
      )
      
      if (countryFeature) {
        resolve({
          countryName: countryFeature.text,
          isNZ: countryFeature.text === 'New Zealand',
          isAU: countryFeature.text === 'Australia',
        })
      }
    })
  })
}
